/**
 * Spec 126/127 — typed client for the Google AIO citation backfill route.
 * Spec 128 — the endpoint is now chunked (keyset cursor + bounded batch), so a
 * large brand never makes a single multi-minute request. `runAioBackfill`
 * drives the batch loop to completion and reports progress.
 *
 *   POST /api/v1/admin/backfill/aio-citations { brand_id, after_id?, limit? }
 *
 * Re-extracts captured `google_aio` responses from stored `aio_sources` (no
 * DataForSEO re-query) and rescores affected domains per period. Admin-gated and
 * brand-access scoped server-side; idempotent.
 */
import { apiFetch } from '../lib/api.js';

/** One batch result from the chunked endpoint. */
export interface AioCitationBatchResult {
  processed: number;
  responses_reanalyzed: number;
  responses_failed: number;
  last_id: string | null;
  done: boolean;
  remaining: number;
  domains_rescored: number;
  periods_rescored: number;
  brands: number;
  /**
   * Spec 205 — how many of this brand's captured Google AI Overview answers
   * still have no recorded search result. Those answers are deliberately left
   * with their old figures, so a non-zero value means the search-records repair
   * should be run first and this one re-run. Returned only on the final batch.
   */
  unresolved_search_status?: number;
}

/** Accumulated totals across the whole backfill loop. */
export interface AioCitationBackfillResult {
  responses_reanalyzed: number;
  responses_failed: number;
  domains_rescored: number;
  periods_rescored: number;
  batches: number;
  /** Spec 205 — carried from the final batch. See AioCitationBatchResult. */
  unresolved_search_status: number;
}

export interface AioBackfillProgress {
  /** Responses re-analyzed so far across all batches. */
  reanalyzed: number;
  /** Live count of responses still queued after the current cursor. */
  remaining: number;
}

/**
 * Run one keyset batch. Throws Error on non-2xx (matching the
 * backfillRuleEmbeddings pattern in ruleIngestion.ts).
 */
export async function backfillAioCitationsBatch(
  brandId: string,
  afterId: string | null = null,
  limit?: number,
): Promise<AioCitationBatchResult> {
  const body: Record<string, unknown> = { brand_id: brandId };
  if (afterId) body.after_id = afterId;
  if (limit) body.limit = limit;
  const res = await apiFetch(`/api/v1/admin/backfill/aio-citations`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`AIO citation backfill failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as { data: AioCitationBatchResult };
  return json.data;
}

/**
 * Drive the chunked backfill to completion: call batches with the returned
 * cursor until `done`, accumulating totals. `onProgress` fires after each batch
 * so the UI can show a live count. A safety cap bounds the loop.
 */
export async function runAioBackfill(
  brandId: string,
  onProgress?: (p: AioBackfillProgress) => void,
  limit?: number,
): Promise<AioCitationBackfillResult> {
  const totals: AioCitationBackfillResult = {
    responses_reanalyzed: 0,
    responses_failed: 0,
    domains_rescored: 0,
    periods_rescored: 0,
    batches: 0,
    unresolved_search_status: 0,
  };
  let cursor: string | null = null;
  // Hard cap: MAX_LIMIT(100) × 10000 ≈ 1M responses — far beyond any real
  // brand; prevents an infinite loop if the server ever returned done=false
  // with no progress.
  for (let i = 0; i < 10000; i++) {
    const batch: AioCitationBatchResult = await backfillAioCitationsBatch(brandId, cursor, limit);
    totals.responses_reanalyzed += batch.responses_reanalyzed;
    totals.responses_failed += batch.responses_failed;
    totals.domains_rescored += batch.domains_rescored;
    totals.periods_rescored += batch.periods_rescored;
    totals.batches += 1;
    onProgress?.({ reanalyzed: totals.responses_reanalyzed, remaining: batch.remaining });
    if (batch.done) {
      totals.unresolved_search_status = batch.unresolved_search_status ?? 0;
      break;
    }
    // No forward progress (defensive): stop rather than spin.
    if (!batch.last_id || batch.last_id === cursor) break;
    cursor = batch.last_id;
  }
  return totals;
}
